import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Head from "next/head";
import nookies from "nookies";
import jwt_decode from "jwt-decode";
import {
  ResponsiveLayout,
  Box,
  Snackbar,
  Banner,
  Typography,
  OverlayProvider,
} from "@bca/ui-library";
import { useRouter } from "next/router";
import { useSelector, useDispatch } from "react-redux";

import Layout from "../src/Layout";
import StockAlert from "../src/StockAlert";
import redirectToIdp from "../src/sso/idp";
import getToken from "../src/sso/getToken";
import getEnvironment from "../src/getEnvironment";
import pushPageView from "../src/analytics/pushPageView";
import TopActionsBarDesktop from "../src/filters/TopActionsBarDesktop";
import AppliedFilters from "../src/filters/AppliedFilters";
import { useViewport } from "../src/hooks/useViewport.hook";
import StickyFooter from "../src/sticky-footer/StickyFooter";
import { DecodedToken } from "../src/authentication.type";
import CardsContainer from "../src/vehicle-result-card/CardsContainer";
import FiltersBarMobile from "../src/filters/FiltersBarMobile";
import ResultCount from "../src/filters/ResultCount";
import SavedSearchesStrip from "../src/SavedSearchesStrip";
import FiltersBarDesktop from "../src/filters/FiltersBarDesktop";
import ClearAllFiltersButton from "../src/filters/ClearAllFiltersButton";
import FiltersBarTablet from "../src/filters/FiltersBarTablet";
import getListOfAppliedFilters from "../src/filters/getListOfAppliedFilters";
import VehiclesPagination from "../src/VehiclesPagination";
import TopActionsBarNonDesktop from "../src/filters/TopActionsBarNonDesktop";
import useSavedSearchList from "../src/save-search/requests/useSavedSearchList.hook";
import { ErrorContext } from "../src/context/ErrorContext";
import SomethingWentWrong from "../src/messages/SomethingWentWrong";
import pushFilterAnalytics from "../src/analytics/pushFilterAnalytics";
import ErrorBanner from "../src/common/components/messages/ErrorBanner";
import getCMSDomain from "../src/vehicle-result-card/getCMSDomain";
import { StoreType } from "../src/StoreType";
import {
  SET_IS_LOADING_FILE,
  SET_IS_PRINT_SERVICE_UNAVAILABLE,
  SET_TOTAL_TRACKED,
  SHOW_BATTERY_GRADE_SLIDEOUT,
  GET_STATE,
  SET_IS_TO_USE_AUTH_ZERO,
} from "../src/redux/slices/appSlice";
import { TrackedVehiclesTitle } from "../src/TrackedVehiclesTitle";
import NoTrackedVehicles from "../src/NoTrackedVehicles";
import { useGetSearchResults } from "../src/hooks/useGetSearchResults";
import { TRACKED } from "../src/constants";
import BatteryGradeSlideout from "../src/BatteryGradeSlideout";
import {
  FeatureFlagManager,
  FeatureFlagProvider,
} from "bca-feature-flag-provider";
import { useAuth } from "@bca-auth0/bca-auth0-provider";
import ErrorPage from "../src/common/ErrorPage/ErrorPage";
import { FEATURE_FLAG_KEYS } from "../src/filters/filtersConfig";
import useUserDataAnalytics from "../src/hooks/useUserDataAnalytics";

const Index: React.FunctionComponent = ({
  user: serverTokenUser,
  aaId,
  ssoId,
  cas,
  userType,
  idToken: serverIdToken,
  isToUseAuthZeroProp,
  authLogoutUrl,
  appConfigurationUrl,
}: any) => {
  const viewportWidth = useViewport(100);
  const errorContext = useContext(ErrorContext);
  const router = useRouter();
  const { query: allQueryStrings } = router;
  const { requestOrigin } = allQueryStrings;
  const isTrackedPage = requestOrigin === TRACKED;

  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [appliedCheckboxes, setAppliedCheckboxes] = useState([]);
  const [showFilters, setshowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(
    Boolean(allQueryStrings["page"]) ? Number(allQueryStrings["page"]) : 1
  );

  const { data: savedSearches } = useSavedSearchList(1000);
  const { searchResults, mutateSearch } = useGetSearchResults(allQueryStrings);
  const dispatch = useDispatch();

  const {
    app: {
      isPrintServiceUnavailable,
      showBatteryGradeSlideout,
      batteryGradeSlideoutData,
      isLoadingFile,
      totalTracked,
      isToUseAuthZero,
    },
  }: StoreType = useSelector(GET_STATE);

  const {
    isAuthenticated,
    authUser,
    error: authError,
    idToken: authIdToken,
  } = useAuth(!isToUseAuthZero);

  const { userAnalyticsData } = useUserDataAnalytics(
    authUser,
    {
      userType: userType,
      aaId: aaId,
      ssoId: ssoId,
      cas: cas,
    },
    isToUseAuthZero
  );

  const user = isToUseAuthZero && isAuthenticated ? authUser : serverTokenUser;
  const idToken =
    isToUseAuthZero && isAuthenticated ? authIdToken : serverIdToken;

  useEffect(() => {
    dispatch(SET_TOTAL_TRACKED(searchResults?.totalTracked));
  }, [searchResults]);

  useEffect(() => {
    if (viewportWidth && viewportWidth === "desktop") {
      setshowFilters(true);
    }
  }, [viewportWidth]);

  useEffect(() => {
    if (isPrintServiceUnavailable) {
      const timer = setTimeout(() => {
        dispatch(SET_IS_PRINT_SERVICE_UNAVAILABLE(false));
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [dispatch, isPrintServiceUnavailable]);

  const updateUrl = useCallback(
    (href: string) => {
      router.push(href, href, { shallow: true });
    },
    [router]
  );

  const isLoading =
    !Boolean(searchResults) &&
    !errorContext?.state.somethingWentWrong &&
    isToUseAuthZero &&
    !isAuthenticated &&
    !authError;

  const getUpdatedPageUrl = (page: number) => {
    let url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    return url.toString();
  };

  const goToPage = (event: any, page: number) => {
    updateUrl(getUpdatedPageUrl(page));
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    setCurrentPage(
      Boolean(allQueryStrings["page"]) ? Number(allQueryStrings["page"]) : 1
    );
  }, [allQueryStrings]);

  const hasPushedRef = useRef(false);
  const hasCompletedInitialLoad = useRef(false);

  const serializedQueryStrings = useMemo(
    () => JSON.stringify(allQueryStrings),
    [allQueryStrings]
  );

  const queryStringRef = useRef(serializedQueryStrings);

  useEffect(() => {
    if (
      hasCompletedInitialLoad.current &&
      queryStringRef.current !== serializedQueryStrings
    ) {
      hasPushedRef.current = false;
      queryStringRef.current = serializedQueryStrings;
    }
  }, [serializedQueryStrings]);

  useEffect(() => {
    if (isLoading || initialLoad) return;
    if (hasPushedRef.current) return;

    if (!hasCompletedInitialLoad.current) {
      hasCompletedInitialLoad.current = true;
      return;
    }

    if (searchResults?.numberOfPages && searchResults?.totalResults) {
      pushFilterAnalytics(
        getListOfAppliedFilters(allQueryStrings),
        allQueryStrings["q"],
        allQueryStrings["page"],
        allQueryStrings["sort"],
        searchResults.numberOfPages,
        searchResults.totalResults
      );

      hasPushedRef.current = true;
    }
  }, [isLoading, searchResults, serializedQueryStrings]);

  useEffect(() => {
    if (initialLoad && !isLoading && searchResults && userAnalyticsData) {
      pushPageView(
        userAnalyticsData.aaId,
        userAnalyticsData.ssoId,
        userAnalyticsData.cas,
        userAnalyticsData.userType,
        isTrackedPage,
        getListOfAppliedFilters(allQueryStrings),
        allQueryStrings["q"],
        allQueryStrings["page"],
        allQueryStrings["sort"],
        searchResults?.numberOfPages,
        searchResults?.totalResults,
        Boolean(allQueryStrings["savedSearchId"]),
        Boolean(allQueryStrings["requestOrigin"] === "Sales")
      );
      setInitialLoad(false);
    }
  }, [initialLoad, isLoading, searchResults, userAnalyticsData]);

  const removeWhiteSpace = (vrm: string) => {
    return vrm?.replace(/\s+/g, "");
  };

  const normalizeVrmNumber = (vrm: string) => {
    return vrm && removeWhiteSpace(vrm).toLowerCase();
  };

  useEffect(() => {
    const vrmNumber = searchResults?.items?.find((res) => {
      return (
        normalizeVrmNumber(res.vrm) ===
        normalizeVrmNumber(allQueryStrings["q"]?.toString())
      );
    });
    if (Boolean(vrmNumber?.vrm)) {
      window.location.assign(
        `${getCMSDomain(
          getEnvironment()
        )}lot/${vrmNumber.vrm.toString()}?searchVersion=new`
      );
    }
  }, [allQueryStrings, searchResults]);

  useEffect(() => {
    let updatedStateFromUrl = getListOfAppliedFilters(allQueryStrings);
    setAppliedCheckboxes([...updatedStateFromUrl]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickShowFilters = () => setshowFilters(!showFilters);

  const handleOnClearAllFilters = () => {
    setAppliedCheckboxes([]);

    let pageUrl = new URL(window.location.href);
    pageUrl.search = isTrackedPage ? "requestOrigin=Tracked" : ""; // remove all query strings

    router.push(pageUrl.href, pageUrl.href, {
      shallow: true,
    });
  };

  const isInErrorState = () => {
    return errorContext?.state.somethingWentWrong;
  };

  const handleSomethingWentWrongRetryClick = () => {
    errorContext?.dispatch({ type: "clear" });
    // Calling mutateSearch will reattempt the search api request, and re-render this component.
    mutateSearch();
  };

  useEffect(() => {
    if (searchResults?.degradedServices?.length > 0) {
      errorContext?.dispatch({
        type: "tracking",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults]);

  useEffect(() => {
    dispatch(SET_IS_TO_USE_AUTH_ZERO(isToUseAuthZeroProp));
  }, [isToUseAuthZeroProp, dispatch]);

  const nrAppliedFacets = getListOfAppliedFilters(allQueryStrings)?.length;
  const hasQueryString = Boolean(allQueryStrings["q"]);

  const filtersCount = nrAppliedFacets + (hasQueryString ? 1 : 0);

  /**
   *  This is presenting a problem with the SomethingWentWrong 'retry' functionality.
   *  I think the overall state of filters and when they're shown needs a little refactoring...
   *
   *  Displaying this FiltersBarMobile like this means this is all the user sees when
   *  they click the 'Retry' button after search error. They should be looking at the skeleton
   *  loaders giving them visual loading feedback.
   */
  if (showFilters && viewportWidth === "mobile") {
    return (
      <FeatureFlagProvider
        clientSecret={appConfigurationUrl}
        keys={[FEATURE_FLAG_KEYS.PHEV]}
      >
        <FiltersBarMobile
          onClose={onClickShowFilters}
          setSelectedVehicles={setSelectedVehicles}
          appliedCheckboxes={appliedCheckboxes}
          setAppliedCheckboxes={setAppliedCheckboxes}
          filtersCount={filtersCount}
          onClearAllFilters={handleOnClearAllFilters}
          resultCount={searchResults?.totalResults}
          searchResultsLoading={isLoading}
        />
      </FeatureFlagProvider>
    );
  }

  const snackbarPadding = `${
    selectedVehicles.length > 0
      ? 115 +
        (viewportWidth === "desktop" || viewportWidth === "smallDesktop"
          ? 0
          : 20)
      : 25
  }px`;

  const handleCloseSnackBar = () => {
    dispatch(SET_IS_LOADING_FILE(false));
  };

  const noTrackedVehicles = isTrackedPage && searchResults?.totalTracked === 0;
  const isGeneralError =
    isInErrorState() && errorContext?.state.type !== "tracking";
  const isTrackingError =
    isInErrorState() && errorContext?.state.type === "tracking";

  const handleCloseBatteryGradeSlideout = () =>
    dispatch(
      SHOW_BATTERY_GRADE_SLIDEOUT({
        showBatteryGradeSlideout: false,
      })
    );

  const batteryGradeSlideout = (
    <OverlayProvider>
      <BatteryGradeSlideout
        isOpen={showBatteryGradeSlideout}
        onClose={handleCloseBatteryGradeSlideout}
        data={batteryGradeSlideoutData}
      />
    </OverlayProvider>
  );

  if (isToUseAuthZero && authError) {
    return (
      <ErrorPage
        user={user}
        idToken={idToken}
        isToUseAuthZero={isToUseAuthZero}
        isAuthenticated={isAuthenticated}
        logoutUrl={authLogoutUrl}
      />
    );
  }

  return (
    <Layout
      user={user}
      idToken={idToken}
      isAuthenticated={isAuthenticated}
      isToUseAuthZero={isToUseAuthZero}
      logoutUrl={authLogoutUrl}
    >
      <FeatureFlagProvider
        clientSecret={appConfigurationUrl}
        keys={[FEATURE_FLAG_KEYS.PHEV]}
      >
        <Head>
          <title>{isTrackedPage ? "Tracked Vehicles" : "Vehicle Search"}</title>
        </Head>
        {isTrackedPage && <TrackedVehiclesTitle />}
        {batteryGradeSlideout}
        {isGeneralError ? (
          <SomethingWentWrong
            onRetryClick={handleSomethingWentWrongRetryClick}
          />
        ) : noTrackedVehicles ? (
          <NoTrackedVehicles />
        ) : (
          <>
            {isTrackingError && <ErrorBanner />}
            {isPrintServiceUnavailable && (
              <Banner
                variant="warning"
                title="Printing and Exporting Services Unavailable"
                primaryLinkLabel="Refresh"
                secondaryLinkLabel="Dismiss"
                primaryLinkAction={() => router.reload()}
                secondaryLinkAction={() =>
                  dispatch(SET_IS_PRINT_SERVICE_UNAVAILABLE(false))
                }
                displayMode="overlay"
                paddingX={60}
                paddingY={
                  viewportWidth === "mobile" || viewportWidth === "tablet"
                    ? 130
                    : 80
                }
              >
                <Typography margin={0} variant="body">
                  We were unable to print or export your selected vehicles.
                  Please refresh the page to try again.
                </Typography>
              </Banner>
            )}
            {isLoadingFile && (
              <Snackbar
                title="Your download will open shortly in a new tab."
                verticalPosition="bottom"
                actionLabel="Dismiss"
                displayTime={10000}
                action={handleCloseSnackBar}
                onClose={handleCloseSnackBar}
                paddingY={snackbarPadding}
                paddingX={viewportWidth === "desktop" ? "90px" : "60px"}
              />
            )}
            {!isTrackedPage && <SavedSearchesStrip />}
            {showFilters &&
              (viewportWidth === "tablet" ||
                viewportWidth === "smallDesktop") && (
                <FiltersBarTablet
                  onClose={onClickShowFilters}
                  isOpen={showFilters}
                  setSelectedVehicles={setSelectedVehicles}
                  appliedCheckboxes={appliedCheckboxes}
                  setAppliedCheckboxes={setAppliedCheckboxes}
                  filtersCount={filtersCount}
                  onClearAllFilters={handleOnClearAllFilters}
                  resultCount={searchResults?.totalResults}
                  searchResultsLoading={isLoading}
                  viewportWidth={viewportWidth}
                />
              )}
            {(viewportWidth === "mobile" || viewportWidth === "tablet") && (
              <TopActionsBarNonDesktop
                showFilters={showFilters}
                onClickShowFilters={onClickShowFilters}
                currentPage={currentPage}
                updateUrl={updateUrl}
                searchResults={searchResults}
                totalTracked={totalTracked}
                savedSearches={savedSearches}
                filterCount={filtersCount}
                viewportWidth={viewportWidth}
                allAppliedFilters={appliedCheckboxes}
                appliedFiltersSlot={
                  <AppliedFilters
                    setSelectedVehicles={setSelectedVehicles}
                    appliedCheckboxes={appliedCheckboxes}
                    setAppliedCheckboxes={setAppliedCheckboxes}
                    clearButtonSlot={
                      filtersCount > 0 ? (
                        <ClearAllFiltersButton
                          filtersCount={filtersCount}
                          onClearAllFilters={handleOnClearAllFilters}
                        />
                      ) : null
                    }
                    allQueryStrings={allQueryStrings}
                  />
                }
              />
            )}
            {(viewportWidth === "smallDesktop" ||
              viewportWidth === "desktop") && (
              <TopActionsBarDesktop
                showFilters={showFilters}
                onClickShowFilters={onClickShowFilters}
                currentPage={currentPage}
                updateUrl={updateUrl}
                searchResults={searchResults}
                totalTracked={totalTracked}
                filterCount={filtersCount}
                handleOnClearAllFilters={handleOnClearAllFilters}
                viewportWidth={viewportWidth}
                savedSearches={savedSearches}
                allAppliedFilters={appliedCheckboxes}
              />
            )}
            {!showFilters &&
              (viewportWidth === "desktop" ||
                viewportWidth === "smallDesktop") && (
                <ResponsiveLayout mb={5}>
                  <AppliedFilters
                    setSelectedVehicles={setSelectedVehicles}
                    appliedCheckboxes={appliedCheckboxes}
                    setAppliedCheckboxes={setAppliedCheckboxes}
                    clearButtonSlot={
                      filtersCount > 0 || viewportWidth === "desktop" ? (
                        <ClearAllFiltersButton
                          filtersCount={filtersCount}
                          onClearAllFilters={handleOnClearAllFilters}
                        />
                      ) : null
                    }
                    allQueryStrings={allQueryStrings}
                  />
                </ResponsiveLayout>
              )}
            <ResponsiveLayout
              height="100%"
              display="flex"
              flexDirection={{
                default: "column",
                desktop: "row",
              }}
              justifyContent={{
                mobile: "flex-start",
                default: "space-between",
              }}
              css={`
                gap: 43px;
              `}
              marginBottom={isTrackedPage ? 50 : 0}
            >
              {showFilters && viewportWidth === "desktop" && (
                <FiltersBarDesktop
                  setSelectedVehicles={setSelectedVehicles}
                  appliedCheckboxes={appliedCheckboxes}
                  setAppliedCheckboxes={setAppliedCheckboxes}
                  searchResultsLoading={isLoading}
                />
              )}
              <Box as="section" width="inherit">
                <CardsContainer
                  loading={false}
                  searchResults={searchResults}
                  allQueryStrings={allQueryStrings}
                  userType={userType}
                  viewportWidth={viewportWidth}
                  selectedVehicles={selectedVehicles}
                  setSelectedVehicles={setSelectedVehicles}
                  currentBidsUrl={searchResults?.currentBidsUrl}
                  showFilters={showFilters}
                />
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent={
                    viewportWidth === "mobile" ? "center" : "space-between"
                  }
                  mt="20px"
                >
                  {viewportWidth !== "mobile" && (
                    <ResultCount
                      resultCount={searchResults?.totalResults}
                      totalTracked={totalTracked}
                      isTrackedPage={isTrackedPage}
                      page={currentPage}
                      pageSize={50}
                    />
                  )}
                  <VehiclesPagination
                    page={currentPage}
                    onPageChange={goToPage}
                    totalPages={searchResults?.numberOfPages}
                    isLoading={isLoading}
                    totalResultCount={searchResults?.totalResults}
                  />
                </Box>
                {!isTrackedPage && viewportWidth && <StockAlert />}
              </Box>
            </ResponsiveLayout>

            {selectedVehicles.length > 0 && (
              <StickyFooter
                selectedVehicles={selectedVehicles}
                setSelectedVehicles={setSelectedVehicles}
                printLink={searchResults?.printSelectedUrl}
                exportLink={searchResults?.exportSelectedUrl}
                pdfEnabled={searchResults?.displayFlags.buyerApiPDFsEnabled}
              />
            )}
          </>
        )}
      </FeatureFlagProvider>
    </Layout>
  );
};

export const getServerSideProps = async (context: any) => {
  const featureFlagManager = new FeatureFlagManager();
  await featureFlagManager.initializeFeatureManager(
    process.env.APP_CONFIGURATION_URL
  );
  const useAuthZeroResult =
    await featureFlagManager.getFeatureFlagStatus("authzero");

  const isToUseAuthZero =
    !useAuthZeroResult.isError && useAuthZeroResult.isEnabled;

  if (isToUseAuthZero) {
    let props: any = {};
    props.authProviderDomain = process.env.AUTH0_DOMAIN;
    props.authProviderClientId = process.env.AUTH0_CLIENT_ID;
    props.authProviderAudience = process.env.AUTH0_AUDIENCE;
    props.authProviderScope = process.env.AUTH0_SCOPE;
    props.redirectUri = process.env.AUTH_PROVIDER_SEARCH_REDIRECT_URI;
    props.isToUseAuthZeroProp = isToUseAuthZero;
    props.authLogoutUrl = process.env.AUTH0_LOGOUT_URL;
    props.appConfigurationUrl = process.env.APP_CONFIGURATION_URL;

    return {
      props,
    };
  } else {
    redirectToIdp(
      context,
      getEnvironment(),
      process.env.VIRTUAL_DOMAIN as string,
      process.env.URL_SUFFIX as string
    );

    let props: any = {};

    const authToken = getToken(nookies.get(context));
    const idToken = nookies.get(context).idtoken || "";

    if (!authToken) {
      return { props: {} };
    }

    let firstName: string = "";
    let lastName: string = "";
    let email = "";
    let userType: string = "";
    let aaId: string = "";
    let ssoId: string = "";
    let cas: string = "";

    if (authToken) {
      const decodedToken = jwt_decode(authToken) as DecodedToken;
      email = decodedToken.email;
      firstName = decodedToken.user_name.split(" ")[0];
      lastName = decodedToken.user_name.split(" ")[1];
      userType = decodedToken?.user_type;
      aaId = decodedToken?.peep_analyticsid;
      ssoId = decodedToken?.sub;
      cas = decodedToken?.sourceaccount_id || null;
    }
    const page = context?.query?.page || null;

    const user = {
      email,
      firstName,
      lastName,
    };

    props = {
      user,
      userType,
      aaId,
      ssoId,
      cas,
      page,
      idToken,
      isToUseAuthZeroProp: isToUseAuthZero,
      appConfigurationUrl: process.env.APP_CONFIGURATION_URL,
    };

    return {
      props,
    };
  }
};

export default Index;
